import { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { Container } from './styles';
import { useSudoku } from '../../../SudokuContext';

interface SquareProps {
  x: number;
  y: number;
  alphabet: string;
  isHint: boolean;
  value: string;
  boxWidth: number;
  boxHeight: number;
  isSquareSolution: string;
}

export const Square = ({
  x,
  y,
  alphabet,
  isHint,
  boxHeight,
  boxWidth,
  value,
  isSquareSolution,
}: SquareProps) => {
  const {
    checkFocus,
    setValuesSquares,
    verifyFinish,
    width,
    height,
    isSudokuFinished,
    showAnswers,
    matrix,
  } = useSudoku();

  // Texto que está presente no quadrado
  const [textInSquare, setTextInSquare] = useState<string>('');
  // Buffer para armazenamento temporário
  const [buffer, setBuffer] = useState<string>('');

  // Função ativada quando o quadrado é focado.
  const handleFocus = () => {
    checkFocus(x, y);
    setBuffer(textInSquare);
    setTextInSquare('');
  };

  // Função ativada quando o quadrado é desfocado.
  const handleBlur = useCallback(() => {
    if (textInSquare === '' && buffer === '' && matrix[x][y].value !== '') {
      setValuesSquares({
        x,
        y,
        newValue: '',
      });
    } else if (textInSquare !== '' && matrix[x][y].value !== textInSquare) {
      setValuesSquares({
        x,
        y,
        newValue: textInSquare,
      });
    } else if (
      textInSquare === '' &&
      buffer !== '' &&
      matrix[x][y].value === buffer
    ) {
      setTextInSquare(buffer);
    }

    verifyFinish();
  }, [buffer, setValuesSquares, textInSquare, verifyFinish, x, y, matrix]);

  // Função que verifica a entrada no input
  // Se for uma letra permitida ele salva no State
  const verifiedInput = (inputValue: string) => {
    const allowedCharacters = alphabet.split('');

    if (allowedCharacters.includes(inputValue)) {
      setTextInSquare(inputValue);
    }
  };
  // Função que verifica se o usuário deu Delete ou Backspace
  const keyCheck = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      setBuffer('');
    }
  };

  /* Estados iniciais para montar o Sudoku */
  useEffect(() => {
    if (isHint) {
      setTextInSquare(isSquareSolution);
    } else {
      value ? setTextInSquare(value) : setTextInSquare('');
    }
  }, [isHint, isSquareSolution, value]);

  // useEffect para verificar se o jogo foi resolvido.
  useEffect(() => {
    if (isSudokuFinished && showAnswers) {
      setTextInSquare(isSquareSolution);
    }
  }, [isSquareSolution, isSudokuFinished, showAnswers]);

  return (
    <Container
      isFirstColumn={y === 0 ? true : false}
      isFirstRow={x === 0 ? true : false}
      isRowDivider={(x + 1) % boxWidth === 0 ? true : false}
      isColumnDivider={(y + 1) % boxHeight === 0 ? true : false}
      isCorrect={isSquareSolution === textInSquare ? true : false}
      itsCorrected={isSudokuFinished}
      isHint={isHint}
      width={width}
      height={height}
    >
      <input
        maxLength={1}
        type="text"
        disabled={isHint || isSudokuFinished}
        value={textInSquare}
        onChange={(event) => verifiedInput(event.target.value)}
        onFocus={() => handleFocus()}
        onBlur={() => handleBlur()}
        onKeyDown={(event) => keyCheck(event)}
      />
    </Container>
  );
};
